import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button, SvgIconUrl } from 'common/components';

import svgIcons from 'common/svgIcons';

import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter';

import { useI18n } from '../useI18n/useI18n.hook';
import './notification.css';
import { NotificationProps } from './types';

const NotificationComponent: React.FC<React.PropsWithChildren<NotificationProps>> = ({
  title = '',
  titleFn = null,
  href,
  description,
  descriptionFn = null,
  onCancel,
  cancelBtnText,
  onConfirm,
  confirmBtnText,
  onClick,
  notificationId,
  icon = svgIcons.Done(),
  customComponent,
  toastId,
  isLoading,
}) => {
  const { push } = useHistory();
  const { translateToText } = useI18n();
  const showDescriptionSection = useMemo(() => description || descriptionFn, [description, descriptionFn]);
  const showButtonsSection = useMemo(() => onCancel || onConfirm, [onCancel, onConfirm]);
  const cancelText = useMemo(
    () => capitalizeFirstLetter(cancelBtnText || translateToText('mobile:notification:button:cancel')),
    [cancelBtnText, translateToText],
  );
  const confirmText = useMemo(
    () => capitalizeFirstLetter(confirmBtnText || translateToText('mobile:notification:button:confirm')),
    [confirmBtnText, translateToText],
  );

  const onClose = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>): void => {
      event.preventDefault();
      event.stopPropagation();

      toast.dismiss(notificationId);
    },
    [notificationId],
  );

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      if (href) {
        toast.dismiss(notificationId);

        if (href.includes('client-area')) {
          window.location.assign(href);
        } else {
          push(href);
        }
        push(href);
      }

      if (onClick) {
        toast.dismiss(notificationId);
        onClick();
      }
    },
    [href, notificationId, onClick, push],
  );

  const onCancelHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      if (onCancel) {
        toast.dismiss(notificationId);
        onCancel();
      }
    },
    [notificationId, onCancel],
  );

  const onConfirmHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      if (onConfirm) {
        toast.dismiss(notificationId);
        onConfirm();
      }
    },
    [notificationId, onConfirm],
  );

  const renderTitle = useCallback(() => {
    if (titleFn) return titleFn(translateToText);
    return capitalizeFirstLetter(title);
  }, [title, titleFn, translateToText]);

  const renderDescription = useCallback(() => {
    if (descriptionFn) return descriptionFn(translateToText);
    return description;
  }, [descriptionFn, translateToText, description]);

  const renderIcon = useCallback(() => {
    if (isLoading || !icon) return null;
    return <SvgIconUrl className='notification__left-icon' symbol={icon} />;
  }, [isLoading, icon]);

  const notificationHeaderClassNames = classNames({
    notification__header: true,
    'notification__header--no-icon': !icon || isLoading,
  });

  if (customComponent) return customComponent;

  return (
    <div className='notification' onClick={onClickHandler} data-test={toastId}>
      <div className={notificationHeaderClassNames} style={!description ? { alignItems: 'center' } : {}}>
        {renderIcon()}
        <div className='notification__text-container'>
          <p className='notification__title H4_med14' data-test='notification__title'>
            {renderTitle()}
          </p>
          {showDescriptionSection && (
            <div className='notification__description Body2_reg_12' data-test='notification__description'>
              {renderDescription()}
            </div>
          )}
        </div>
        <SvgIconUrl className='notification__right-icon' symbol={svgIcons.Close()} onClick={onClose} />
      </div>
      {showButtonsSection && (
        <div className='notification__buttons' data-test='notification__buttons'>
          {onCancel && (
            <Button className='notification__button' data-test='notification__button-cancel' onClick={onCancelHandler}>
              {cancelText}
            </Button>
          )}
          {onConfirm && (
            <Button
              color='transparent'
              className='notification__button'
              data-test='notification__button-confirm'
              onClick={onConfirmHandler}
            >
              {confirmText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(NotificationComponent);
